import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft,
  AlertCircle,
  Upload,
  FileText,
  X,
  Tag as TagIcon,
  Eye,
  Download,
} from "lucide-react";
import { api } from "@/api/client";
import apiClient from "@/api/client";
import SearchableMultiSelect from "@/components/ui/SearchableMultiSelect";
import TaxonomyCascadePicker, {
  type SelectedNode as TaxonomySelectedNode,
} from "@/components/taxonomy/TaxonomyCascadePicker";
// import ClassificationSuggestionPanel from "@/components/documents/ClassificationSuggestionPanel"; // hidden for now
import type {
  Document,
  JenisPNBP,
  SiklusKeuangan,
  BentukDokumen,
  KategoriInformasi,
  SubKategori,
  Dimensi,
  Tag,
} from "@/types/document";

const editSchema = z.object({
  judul: z.string().min(3, "Judul minimal 3 karakter").max(500),
  deskripsi: z.string().optional().nullable(),
  nomor_dokumen: z.string().optional().nullable(),
  // Legacy & opsional: klasifikasi pakai taxonomy node; jenis diturunkan di server.
  jenis_pnbp_id: z.string().optional(),
  siklus_keuangan_id: z.string().uuid("Pilih siklus keuangan"),
  bentuk_dokumen_id: z.string().uuid("Pilih bentuk dokumen"),
  kategori_informasi_id: z.string().optional().nullable(),
  sub_kategori_id: z.string().optional().nullable(),
  taxonomy_node_ids: z
    .array(z.string().uuid())
    .min(1, "Pilih minimal satu node Pohon Taxonomy PNBP")
    .default([]),
  primary_taxonomy_node_id: z.string().uuid().optional(),
  tingkat_kerahasiaan: z.enum(["PUBLIC", "INTERNAL", "CONFIDENTIAL", "SECRET"]),
  dimensi_ids: z.array(z.string().uuid()).default([]),
  tag_ids: z.array(z.string().uuid()).default([]),
  free_tags: z.array(z.string()).default([]),
  pic_ids: z.array(z.string().uuid()).default([]),
  stakeholder_ids: z.array(z.string().uuid()).default([]),
  unit_kerja_ids: z.array(z.string().uuid()).default([]),
  tanggal_dokumen: z.string().optional().nullable(),
  tanggal_berlaku: z.string().optional().nullable(),
  tanggal_berakhir: z.string().optional().nullable(),
});

type EditFormValues = z.infer<typeof editSchema>;

export default function DocumentEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: doc, isLoading } = useQuery<Document>({
    queryKey: ["document", id],
    queryFn: () => api.get(`/documents/${id}`),
    enabled: !!id,
  });

  const { data: jenisList = [] } = useQuery<JenisPNBP[]>({
    queryKey: ["master", "jenis-pnbp"],
    queryFn: () => api.get("/master/jenis-pnbp"),
  });
  const { data: siklusList = [] } = useQuery<SiklusKeuangan[]>({
    queryKey: ["master", "siklus-keuangan"],
    queryFn: () => api.get("/master/siklus-keuangan"),
  });
  const { data: bentukList = [] } = useQuery<BentukDokumen[]>({
    queryKey: ["master", "bentuk-dokumen"],
    queryFn: () => api.get("/master/bentuk-dokumen"),
  });
  const { data: kategoriList = [] } = useQuery<KategoriInformasi[]>({
    queryKey: ["master", "kategori-informasi"],
    queryFn: () => api.get("/master/kategori-informasi"),
  });
  const { data: dimensiList = [] } = useQuery<Dimensi[]>({
    queryKey: ["master", "dimensi"],
    queryFn: () => api.get("/master/dimensi"),
  });
  const { data: tagList = [] } = useQuery<Tag[]>({
    queryKey: ["tags"],
    queryFn: () => api.get("/tags/"),
  });
  const { data: picList = [] } = useQuery<
    { id: string; nama: string; jabatan?: string }[]
  >({
    queryKey: ["master", "pic"],
    queryFn: () => api.get("/master/pic"),
  });
  const { data: stakeholderList = [] } = useQuery<
    { id: string; nama: string; tipe?: string; parent_id?: string | null }[]
  >({
    queryKey: ["master", "stakeholder"],
    queryFn: () => api.get("/master/stakeholder"),
  });
  const { data: unitKerjaList = [] } = useQuery<
    { id: string; kode: string; nama: string }[]
  >({
    queryKey: ["master", "unit-kerja"],
    queryFn: () => api.get("/master/unit-kerja"),
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
  });

  const watchedKategoriId = watch("kategori_informasi_id");
  const { data: subKategoriList = [] } = useQuery<SubKategori[]>({
    queryKey: ["master", "sub-kategori", watchedKategoriId],
    queryFn: () =>
      api.get("/master/sub-kategori", { kategori_id: watchedKategoriId }),
    enabled: !!watchedKategoriId,
  });

  // Pohon Taxonomy PNBP (m2m, dengan penanda primer). Sama spt form create:
  // Jenis PNBP diturunkan otomatis dari node primer.
  const [taxonomySelected, setTaxonomySelected] = useState<TaxonomySelectedNode[]>([]);

  const derivedJenis = (() => {
    const primary = taxonomySelected.find((t) => t.is_primary) ?? taxonomySelected[0];
    if (!primary) return null;
    const jenisKodeRaw = primary.kodes.find((k) => /^\d+$/.test(k));
    const jenisKode = jenisKodeRaw
      ? `J${jenisKodeRaw.padStart(2, "0")}`
      : primary.kodes.includes("PNBP")
        ? "J00"
        : null;
    if (!jenisKode) return null;
    return jenisList.find((j) => j.kode === jenisKode) ?? null;
  })();

  // Sinkronkan pilihan taxonomy → field form (ids, primer) + derive Jenis PNBP.
  useEffect(() => {
    const ids = taxonomySelected.map((t) => t.id);
    const primary = taxonomySelected.find((t) => t.is_primary)?.id;
    setValue("taxonomy_node_ids", ids);
    setValue("primary_taxonomy_node_id", primary);
    if (taxonomySelected.length > 0) {
      setValue("jenis_pnbp_id", derivedJenis?.id ?? "", {
        shouldValidate: !!derivedJenis,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxonomySelected, derivedJenis?.id]);

  useEffect(() => {
    if (doc) {
      setTaxonomySelected(
        (doc.taxonomy_nodes ?? []).map((t) => ({
          id: t.id,
          label: t.label,
          is_primary: t.is_primary,
          kodes: t.kodes,
        })),
      );
      reset({
        judul: doc.judul,
        deskripsi: doc.deskripsi || "",
        nomor_dokumen: doc.nomor_dokumen || "",
        jenis_pnbp_id: doc.jenis_pnbp_id || "",
        siklus_keuangan_id: doc.siklus_keuangan_id || "",
        bentuk_dokumen_id: doc.bentuk_dokumen_id || "",
        kategori_informasi_id: doc.kategori_informasi_id || "",
        sub_kategori_id: doc.sub_kategori_id || "",
        tingkat_kerahasiaan: doc.tingkat_kerahasiaan as any,
        dimensi_ids: doc.dimensi?.map((d) => d.id) || [],
        tag_ids: [
          ...new Set(
            doc.tags?.filter((t) => t.kategori !== "custom").map((t) => t.id) || [],
          ),
        ],
        free_tags: [
          ...new Set(
            doc.tags?.filter((t) => t.kategori === "custom").map((t) => t.nama) || [],
          ),
        ],
        pic_ids: doc.pic?.map((p) => p.id) || [],
        stakeholder_ids: doc.stakeholders?.map((s) => s.id) || [],
        unit_kerja_ids: doc.unit_kerja?.map((u) => u.id) || [],
        tanggal_dokumen: doc.tanggal_dokumen?.split("T")[0] || "",
        tanggal_berlaku: doc.tanggal_berlaku?.split("T")[0] || "",
        tanggal_berakhir: doc.tanggal_berakhir?.split("T")[0] || "",
      });

      // Initialise the parent dropdown from the saved stakeholder
      const savedId = doc.stakeholders?.[0]?.id;
      if (savedId && stakeholderList.length > 0) {
        const saved = stakeholderList.find((s) => s.id === savedId);
        setSelectedStakeholderParentId(saved?.parent_id ?? savedId);
      }
    }
  }, [doc, reset, stakeholderList]);

  const [selectedStakeholderParentId, setSelectedStakeholderParentId] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [freeTagInput, setFreeTagInput] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState("");
  const queryClient = useQueryClient();

  // ── AI menyarankan dari file baru (non-destruktif: usulan, bukan auto-isi) ──
  type Suggestion = {
    key: string;
    label: string;
    current: string;
    suggested: string;
    apply: () => void;
  };
  const [aiStatus, setAiStatus] = useState<"idle" | "analyzing" | "ready" | "error">("idle");
  const [aiError, setAiError] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  async function analyzeNewFile(file: File) {
    setAiStatus("analyzing");
    setAiError("");
    setSuggestions([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { job_id } = await api.upload<{ job_id: string }>(
        "/documents/analyze-file", fd, { timeout: 900_000 },
      );
      pollAi(job_id, 0);
    } catch {
      setAiStatus("error");
      setAiError("Analisis AI gagal. Anda tetap bisa menyimpan & mengganti file manual.");
    }
  }
  function pollAi(jobId: string, attempts: number) {
    pollTimerRef.current = setTimeout(async () => {
      if (attempts > 400) {
        setAiStatus("error");
        setAiError("Analisis AI memakan waktu terlalu lama.");
        return;
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res = await api.get<any>(`/documents/analyze-jobs/${jobId}`);
        if (res.status === "processing") return pollAi(jobId, attempts + 1);
        if (res.status === "done" && res.result) buildSuggestions(res.result);
        else {
          setAiStatus("error");
          setAiError(res.error ?? "Analisis AI gagal.");
        }
      } catch {
        setAiStatus("error");
        setAiError("Analisis AI gagal.");
      }
    }, 3000);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function buildSuggestions(result: any) {
    const out: Suggestion[] = [];
    const push = (key: string, label: string, current: string, suggested: string, apply: () => void) => {
      if (suggested && suggested.trim() && suggested.trim() !== (current || "").trim())
        out.push({ key, label, current: current || "—", suggested, apply });
    };
    const m = result.metadata ?? {};
    const c = result.classification ?? {};
    const valid = (v?: string | null) => (v && v !== "null" && v !== "None" ? v : "");

    push("judul", "Judul", watch("judul") || "", valid(m.judul), () => setValue("judul", m.judul));
    push("nomor_dokumen", "Nomor", watch("nomor_dokumen") || "", valid(m.nomor_dokumen),
      () => setValue("nomor_dokumen", m.nomor_dokumen));
    for (const [f, lbl] of [["tanggal_dokumen", "Tgl Dokumen"], ["tanggal_berlaku", "Tgl Berlaku"], ["tanggal_berakhir", "Tgl Berakhir"]] as const) {
      const sv = valid(m[f]);
      push(f, lbl, (watch(f) as string) || "", sv, () => setValue(f, sv));
    }
    if (c.siklus) {
      const match = siklusList.find((s) => s.kode.toLowerCase() === String(c.siklus).toLowerCase());
      const cur = siklusList.find((s) => s.id === watch("siklus_keuangan_id"));
      if (match) push("siklus", "Siklus Keuangan", cur ? `${cur.kode} — ${cur.nama}` : "", `${match.kode} — ${match.nama}`,
        () => setValue("siklus_keuangan_id", match.id));
    }
    if (c.bentuk) {
      const match = bentukList.find((b) => b.kode.toLowerCase() === String(c.bentuk).toLowerCase());
      const cur = bentukList.find((b) => b.id === watch("bentuk_dokumen_id"));
      if (match) push("bentuk", "Bentuk Dokumen", cur ? `${cur.kode} — ${cur.nama}` : "", `${match.kode} — ${match.nama}`,
        () => setValue("bentuk_dokumen_id", match.id));
    }
    if (Array.isArray(c.dimensi) && c.dimensi.length) {
      const ids = c.dimensi.map((k: string) => dimensiList.find((d) => d.kode.toLowerCase() === k.toLowerCase())).filter(Boolean).map((d: { id: string }) => d.id);
      const sNames = dimensiList.filter((d) => ids.includes(d.id)).map((d) => d.nama).join(", ");
      const curNames = dimensiList.filter((d) => ((watch("dimensi_ids") as string[]) || []).includes(d.id)).map((d) => d.nama).join(", ");
      if (ids.length) push("dimensi", "Dimensi", curNames, sNames, () => setValue("dimensi_ids", ids));
    }
    if (result.summary) {
      push("deskripsi", "Deskripsi", watch("deskripsi") || "", result.summary, () => setValue("deskripsi", result.summary));
    }
    setSuggestions(out);
    setAiStatus(out.length ? "ready" : "idle");
  }
  function applyOne(s: Suggestion) {
    s.apply();
    setSuggestions((prev) => prev.filter((x) => x.key !== s.key));
  }
  function applyAll() {
    suggestions.forEach((s) => s.apply());
    setSuggestions([]);
  }

  const handlePreview = async () => {
    const res = await apiClient.get(`/documents/${id}/preview`, {
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    setPreviewUrl(window.URL.createObjectURL(blob));
    setPreviewType(res.headers["content-type"] || "");
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewType("");
  };

  const handleDownload = async () => {
    const res = await apiClient.get(`/documents/${id}/download`, {
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc?.file_name || "document";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const updateMutation = useMutation({
    mutationFn: async (data: EditFormValues) => {
      await api.put(`/documents/${id}`, data, { timeout: 900000 });
      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        await api.upload(`/documents/${id}/upload`, formData);
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["documents"] }),
        queryClient.invalidateQueries({ queryKey: ["document", id] }),
        queryClient.invalidateQueries({ queryKey: ["document-versions", id] }),
        queryClient.invalidateQueries({ queryKey: ["document-changes", id] }),
        queryClient.invalidateQueries({ queryKey: ["approval-history", id] }),
      ]);
      navigate(`/documents/${id}`);
    },
  });

  const onSubmit = (data: EditFormValues) => {
    // Mode tinjau-ulang + ganti file: konfirmasi karena status balik ke DRAFT.
    if (reReviewMode && selectedFile) {
      const ok = window.confirm(
        `Mengganti file pada dokumen ${doc?.status_kode} akan mengembalikannya ke DRAFT untuk ditinjau ulang. Lanjutkan?`,
      );
      if (!ok) return;
    }
    // Commit any un-added free tag still sitting in the input
    const pending = freeTagInput.trim();
    const free_tags = [...(data.free_tags || [])];
    if (pending && !free_tags.includes(pending)) {
      free_tags.push(pending);
    }
    setFreeTagInput("");
    // jenis_pnbp_id legacy diturunkan dari taxonomy node di server — jangan kirim.
    // Field UUID opsional lain: kirim null (bukan "") supaya lolos validasi UUID.
    const { jenis_pnbp_id: _omitJenis, ...rest } = data;
    void _omitJenis;
    updateMutation.mutate({
      ...rest,
      free_tags,
      kategori_informasi_id: data.kategori_informasi_id || null,
      sub_kategori_id: data.sub_kategori_id || null,
    });
  };

  if (isLoading || !doc) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Only allow editing when document is in DRAFT or DITOLAK status
  // DRAFT/DITOLAK: edit penuh. DISETUJUI/DITERBITKAN: mode "ganti file / tinjau
  // ulang" (boleh ganti file + terapkan saran AI; menyimpan mengembalikan ke
  // DRAFT untuk ditinjau ulang). DIAJUKAN/DITINJAU (sedang proses): diblok.
  const reReviewMode =
    doc.status_kode === "DISETUJUI" || doc.status_kode === "DITERBITKAN";
  const editable =
    doc.status_kode === "DRAFT" || doc.status_kode === "DITOLAK" || reReviewMode;
  if (!editable) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <AlertCircle className="h-8 w-8 text-amber-500" />
        <p className="text-sm text-gray-600">
          Dokumen tidak dapat diedit karena sedang dalam proses persetujuan.
        </p>
        <button
          onClick={() => navigate(`/documents/${id}`)}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          Kembali ke Detail
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/documents/${id}`)}
          className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Dokumen</h1>
          {doc?.taxonomy_breadcrumb && doc.taxonomy_breadcrumb.length > 0 && (
            <p className="mt-1 text-xs text-gray-500">
              Domain: {doc.taxonomy_breadcrumb.join(" / ")}
            </p>
          )}
        </div>
      </div>

      {/* Banner mode tinjau ulang (dokumen sudah disetujui/diterbitkan) */}
      {reReviewMode && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-4 text-amber-800">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <span className="text-sm">
            Dokumen ini berstatus <strong>{doc.status_kode}</strong>. Mengganti file
            akan mengembalikannya ke <strong>DRAFT</strong> untuk ditinjau ulang.
            Versi & perubahan sebelumnya tetap tercatat.
          </span>
        </div>
      )}

      {/* Panel saran AI dari file baru (non-destruktif) */}
      {(aiStatus === "analyzing" || aiStatus === "error" || suggestions.length > 0) && (
        <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
          <p className="text-sm font-semibold text-blue-900 mb-2">
            Saran AI dari file baru
          </p>
          {aiStatus === "analyzing" && (
            <p className="text-xs text-blue-700">Menganalisis file… (bisa beberapa menit)</p>
          )}
          {aiStatus === "error" && <p className="text-xs text-amber-700">{aiError}</p>}
          {suggestions.length > 0 && (
            <>
              <div className="flex justify-end mb-2">
                <button
                  type="button"
                  onClick={applyAll}
                  className="text-xs font-medium text-blue-700 hover:text-blue-900"
                >
                  Terapkan Semua
                </button>
              </div>
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <div
                    key={s.key}
                    className="flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-white px-3 py-2"
                  >
                    <div className="min-w-0 text-xs">
                      <span className="font-medium text-gray-600">{s.label}: </span>
                      <span className="text-gray-400 line-through">{s.current}</span>
                      <span className="mx-1 text-gray-400">→</span>
                      <span className="font-semibold text-gray-800">{s.suggested}</span>
                    </div>
                    <div className="flex flex-shrink-0 gap-1.5">
                      <button
                        type="button"
                        onClick={() => applyOne(s)}
                        className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
                      >
                        Terapkan
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setSuggestions((prev) => prev.filter((x) => x.key !== s.key))
                        }
                        className="rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50"
                      >
                        Abaikan
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {updateMutation.isError && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 p-4 mb-4 text-red-700">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">
              Gagal menyimpan perubahan. Silakan coba lagi.
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Judul */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Judul Dokumen <span className="text-red-500">*</span>
            </label>
            <input
              {...register("judul")}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            {errors.judul && (
              <p className="mt-1 text-xs text-red-600">
                {errors.judul.message}
              </p>
            )}
          </div>

          {/* Nomor Dokumen */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nomor Dokumen
            </label>
            <input
              {...register("nomor_dokumen")}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          {/* Pohon Taxonomy PNBP */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pohon Taxonomy PNBP <span className="text-red-500">*</span>
              <span className="ml-2 text-xs font-normal text-gray-500">
                (mulai dari root PNBP lalu Jenis PNBP; boleh berhenti di level mana saja — tidak harus sampai paling spesifik. Bintang ⭐ menandai primer)
              </span>
            </label>
            <TaxonomyCascadePicker
              value={taxonomySelected}
              onChange={setTaxonomySelected}
              taxonomyType="PNBP_DOMAIN"
              leafOnly={false}
            />
            {(() => {
              const primary =
                taxonomySelected.find((t) => t.is_primary) ?? taxonomySelected[0];
              if (!primary)
                return (
                  <p className="mt-2 text-xs text-amber-600">
                    Wajib pilih minimal satu node Pohon Taxonomy PNBP.
                  </p>
                );
              const leaf = primary.label.split(" / ").pop();
              return (
                <p className="mt-2 text-xs text-emerald-700">
                  ✓ Klasifikasi: <strong>{leaf}</strong>
                  <span className="text-emerald-600/80"> — {primary.label}</span>
                </p>
              );
            })()}
            {errors.taxonomy_node_ids && (
              <p className="mt-1 text-xs text-red-600">
                {errors.taxonomy_node_ids.message as string}
              </p>
            )}
          </div>

          {/* Classification Row (Jenis di-derive dari taxonomy, sama spt create) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Siklus Keuangan <span className="text-red-500">*</span>
              </label>
              <select
                {...register("siklus_keuangan_id")}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Pilih Siklus</option>
                {siklusList.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.kode} — {s.nama}
                  </option>
                ))}
              </select>
              {errors.siklus_keuangan_id && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.siklus_keuangan_id.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bentuk Dokumen <span className="text-red-500">*</span>
              </label>
              <select
                {...register("bentuk_dokumen_id")}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Pilih Bentuk</option>
                {bentukList.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.kode} — {b.nama}
                  </option>
                ))}
              </select>
              {errors.bentuk_dokumen_id && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.bentuk_dokumen_id.message}
                </p>
              )}
            </div>
          </div>

          {/* Kategori Informasi & Sub-Kategori */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategori Informasi
              </label>
              <select
                {...register("kategori_informasi_id", {
                  onChange: () => setValue("sub_kategori_id", ""),
                })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Pilih Kategori</option>
                {kategoriList.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.kode.replace("K", "")}. {k.nama}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sub-Kategori
              </label>
              <select
                {...register("sub_kategori_id")}
                disabled={!watchedKategoriId}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <option value="">
                  {watchedKategoriId
                    ? "Pilih Sub-Kategori"
                    : "Pilih Kategori terlebih dahulu"}
                </option>
                {subKategoriList.map((sk) => (
                  <option key={sk.id} value={sk.id}>
                    {sk.nama}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Dimensi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dimensi Fungsional
            </label>
            <Controller
              name="dimensi_ids"
              control={control}
              render={({ field }) => (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {dimensiList.map((d) => (
                    <label
                      key={d.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600"
                        checked={(field.value || []).includes(d.id)}
                        onChange={(e) => {
                          const val = field.value || [];
                          if (e.target.checked) field.onChange([...val, d.id]);
                          else
                            field.onChange(
                              val.filter((id: string) => id !== d.id),
                            );
                        }}
                      />
                      <span className="text-sm text-gray-700">
                        <span className="font-mono text-xs text-gray-500">
                          {d.kode}
                        </span>{" "}
                        {d.nama}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            />
          </div>

          {/* Unit Kerja */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit Kerja
            </label>
            <Controller
              name="unit_kerja_ids"
              control={control}
              render={({ field }) => (
                <SearchableMultiSelect
                  options={unitKerjaList.map((u) => ({
                    value: u.id,
                    label: u.nama,
                    hint: u.kode,
                  }))}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Pilih unit kerja..."
                />
              )}
            />
          </div>

          {/* PIC */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PIC (Person in Charge)
            </label>
            <Controller
              name="pic_ids"
              control={control}
              render={({ field }) => (
                <SearchableMultiSelect
                  options={picList.map((p) => ({
                    value: p.id,
                    label: p.jabatan ? `${p.nama} — ${p.jabatan}` : p.nama,
                  }))}
                  value={field.value || []}
                  onChange={field.onChange}
                  placeholder="Pilih PIC..."
                />
              )}
            />
          </div>

          {/* Stakeholder */}
          <Controller
            name="stakeholder_ids"
            control={control}
            render={({ field }) => {
              const parentStakeholders = stakeholderList.filter((s) => !s.parent_id);
              const childOptions = selectedStakeholderParentId
                ? stakeholderList.filter((s) => s.parent_id === selectedStakeholderParentId)
                : [];
              const currentChildId =
                field.value?.[0] && childOptions.some((c) => c.id === field.value[0])
                  ? field.value[0]
                  : "";

              return (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stakeholder
                    </label>
                    <select
                      value={selectedStakeholderParentId}
                      onChange={(e) => {
                        const pid = e.target.value;
                        setSelectedStakeholderParentId(pid);
                        field.onChange(pid ? [pid] : []);
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Pilih stakeholder...</option>
                      {parentStakeholders.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.tipe ? `${s.nama} (${s.tipe})` : s.nama}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sub-Stakeholder
                    </label>
                    <select
                      value={currentChildId}
                      disabled={childOptions.length === 0}
                      onChange={(e) => {
                        const cid = e.target.value;
                        field.onChange(cid ? [cid] : [selectedStakeholderParentId]);
                      }}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      <option value="">
                        {childOptions.length > 0
                          ? "Pilih sub-stakeholder (opsional)"
                          : "Pilih stakeholder terlebih dahulu"}
                      </option>
                      {childOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.tipe ? `${c.nama} (${c.tipe})` : c.nama}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            }}
          />

          {/* Tingkat Kerahasiaan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tingkat Kerahasiaan <span className="text-red-500">*</span>
            </label>
            <select
              {...register("tingkat_kerahasiaan")}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="PUBLIC">Publik</option>
              <option value="INTERNAL">Internal</option>
              <option value="CONFIDENTIAL">Rahasia</option>
              <option value="SECRET">Sangat Rahasia</option>
            </select>
          </div>

          {/* Tags Terstruktur */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tag Terstruktur
            </label>
            <Controller
              name="tag_ids"
              control={control}
              render={({ field }) => (
                <div className="flex flex-wrap gap-2">
                  {tagList
                    .filter((t) => (t as any).kategori !== "custom")
                    .map((tag) => (
                      <label
                        key={tag.id}
                        className="flex items-center gap-1 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600"
                          checked={(field.value || []).includes(tag.id)}
                          onChange={(e) => {
                            const val = field.value || [];
                            if (e.target.checked)
                              field.onChange([...val, tag.id]);
                            else
                              field.onChange(
                                val.filter((id: string) => id !== tag.id),
                              );
                          }}
                        />
                        <span
                          className="text-sm px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: tag.warna
                              ? `${tag.warna}20`
                              : "#f3f4f6",
                          }}
                        >
                          {tag.nama}
                        </span>
                      </label>
                    ))}
                </div>
              )}
            />
          </div>

          {/* Tag Bebas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tag Bebas
            </label>
            <Controller
              name="free_tags"
              control={control}
              render={({ field }) => {
                const list: string[] = field.value || [];
                const addTag = () => {
                  const trimmed = freeTagInput.trim();
                  if (trimmed && !list.includes(trimmed)) {
                    field.onChange([...list, trimmed]);
                  }
                  setFreeTagInput("");
                };
                return (
                  <>
                    <div className="flex gap-2">
                      <input
                        value={freeTagInput}
                        onChange={(e) => setFreeTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addTag();
                          }
                        }}
                        className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                        placeholder="Ketik tag dan tekan Enter..."
                      />
                      <button
                        type="button"
                        onClick={addTag}
                        className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
                      >
                        Tambah
                      </button>
                    </div>
                    {list.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {list.map((tag) => (
                          <span
                            key={tag}
                            className="flex items-center gap-1 rounded-full bg-sky-100 px-3 py-1 text-xs text-sky-800"
                          >
                            <TagIcon className="h-3 w-3" />
                            {tag}
                            <button
                              type="button"
                              onClick={() =>
                                field.onChange(list.filter((t) => t !== tag))
                              }
                              className="ml-1 hover:text-sky-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                );
              }}
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tanggal Dokumen
              </label>
              <input
                type="date"
                {...register("tanggal_dokumen")}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tanggal Berlaku
              </label>
              <input
                type="date"
                {...register("tanggal_berlaku")}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tanggal Berakhir
              </label>
              <input
                type="date"
                {...register("tanggal_berakhir")}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Deskripsi */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Deskripsi
            </label>
            <textarea
              {...register("deskripsi")}
              rows={3}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Dokumen
            </label>
            {doc.file_name && !selectedFile && (
              <div className="flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 mb-3">
                <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {doc.file_name}
                  </p>
                  {doc.file_size && (
                    <p className="text-xs text-gray-500">
                      {(doc.file_size / 1024 / 1024).toFixed(2)} MB •{" "}
                      {doc.mime_type}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <button
                    type="button"
                    onClick={handlePreview}
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </button>
                </div>
              </div>
            )}
            {selectedFile ? (
              <div className="flex items-center gap-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3">
                <Upload className="h-5 w-5 text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-800 truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-blue-600">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB — versi
                    baru
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  className="text-blue-400 hover:text-blue-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 px-4 py-4 cursor-pointer hover:border-gray-400 transition-colors">
                <Upload className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {doc.file_name
                    ? "Klik untuk upload versi baru"
                    : "Klik untuk upload file"}
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.docx,.xlsx,.jpg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                      // Jalankan analisis AI utk MENYARANKAN pembaruan field (non-destruktif).
                      analyzeNewFile(file);
                    }
                  }}
                />
              </label>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/documents/${id}`)}
              className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSubmitting || updateMutation.isPending}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting || updateMutation.isPending
                ? "Menyimpan..."
                : "Simpan Perubahan"}
            </button>
          </div>
        </form>
      </div>

      {/* Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="relative bg-white rounded-xl shadow-2xl w-[90vw] h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">
                Preview — {doc.file_name}
              </h3>
              <button
                onClick={closePreview}
                className="rounded-lg p-1.5 hover:bg-gray-100"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-1">
              {previewType.includes("pdf") ? (
                <iframe src={previewUrl} className="w-full h-full rounded" />
              ) : previewType.includes("image") ? (
                <img
                  src={previewUrl}
                  alt={doc.file_name}
                  className="max-w-full max-h-full mx-auto object-contain"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 text-sm">
                  Preview tidak tersedia untuk tipe file ini.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
